import { defineStore } from "pinia";
import { ref } from "vue";
import type {
    APIResponse, EventType, PredioGeoJson, PrediosList, EventsFilter,
    Evento, RegionList, RegionGeoJson, ComunaGeoJson, ComunaList,
    GeoJson, FeatureCollection, UnitsOnlineResponse, Predio, PhotoFile,
    PhotoFilesResponse, Unit,
    PhotoFilesFilter
} from "../types";
import API from "../services";
import { AxiosError } from "axios";

export const useServicesStore = defineStore("servicesStore", () => {
    // Definir referencias para cada tipo de datos
    const events = ref<Evento[]>([]);
    const eventsPropios = ref<Evento[]>([]);
    const eventTypes = ref<EventType[]>([]);
    const prediosList = ref<Predio[]>([])
    const predios = ref<PrediosList>();
    const regiones = ref<RegionList>();
    const comunas = ref<ComunaList>();
    const ciudades = ref<FeatureCollection>();
    const carreteras = ref<FeatureCollection>();
    const caminosPrincipales = ref<FeatureCollection>();
    const caminosVecinales = ref<FeatureCollection>();
    const aguas = ref<FeatureCollection>();
    const unitsOnline = ref<Unit[]>([]);
    const photoFiles = ref<PhotoFile[]>([]);
    const cantidadPhotos = ref(0);

    // Funciones de inicialización
    function initEvents(data: Evento[]) {
        console.log("EVENTOS:" + data.length)
        events.value = data;
    }
    function initEventsPropios(data: Evento[]) {
        console.log("EVENTOS PROPIOS:" + data.length)
        eventsPropios.value = data;
    }
    function initEventTypes(data: EventType[]) {
        eventTypes.value = data;
    }
    function initPrediosList(data: Predio[]) {
        prediosList.value = data;
    }
    function initPredios(data: PredioGeoJson[]) {
        predios.value = {
            type: "FeatureCollection",
            features: data
        }
    }

    function initRegiones(data: RegionGeoJson[]) {
        regiones.value = {
            type: "FeatureCollection",
            features: data
        }
    }

    function initComunas(data: ComunaGeoJson[]) {
        comunas.value = {
            type: "FeatureCollection",
            features: data
        };
    }

    function initCiudades(data: GeoJson[]) {
        ciudades.value = {
            type: "FeatureCollection",
            features: data
        };
    }

    function initCarreteras(data: GeoJson[]) {
        carreteras.value = {
            type: "FeatureCollection",
            features: data
        };
    }

    function initCaminosPrincipales(data: GeoJson[]) {
        caminosPrincipales.value = {
            type: "FeatureCollection",
            features: data
        };
    }

    function initCaminosVecinales(data: GeoJson[]) {
        caminosVecinales.value = {
            type: "FeatureCollection",
            features: data
        };
    }

    function initAguas(data: GeoJson[]) {
        aguas.value = {
            type: "FeatureCollection",
            features: data
        };
    }

    function initUnitsOnline(data: Unit[]) {
        unitsOnline.value = data;
    }


    async function dispatchGetEvents(filtros: EventsFilter): Promise<APIResponse<null>> {
        try {
            const { status, data } = await API.getEvents(filtros);
            if (status === 200) {
                // Extrae directamente el array de eventos
                const eventos = Array.isArray(data.Entries?.Entry)
                    ? data.Entries.Entry
                    : data.Entries?.Entry ? [data.Entries.Entry] : [];

                initEvents(eventos);
                return {
                    success: true,
                    content: null,
                };
            } else {
                console.error("Error en respuesta getEvents, status:", status, data);
            }
        } catch (error) {
            const _error = error as AxiosError<string>;
            console.error("Error en dispatchGetEvents:", _error.message, _error.response?.data);
            return {
                success: false,
                status: _error.response?.status,
                content: null,
            };
        }
        return {
            success: false,
            content: null,
            status: 400,
        };
    }
    async function dispatchGetEventsPropios(startDate: string, endDate: string, userId: string): Promise<APIResponse<null>> {
        try {
            const { status, data } = await API.getEventsPropios(startDate, endDate, userId);
            if (status === 200) {
                // Extrae directamente el array de eventos
                const eventos = Array.isArray(data.Entries?.Entry)
                    ? data.Entries.Entry
                    : data.Entries?.Entry ? [data.Entries.Entry] : [];

                initEventsPropios(eventos);
                return {
                    success: true,
                    content: null,
                };
            } else {
                console.error("Error en respuesta getEvents, status:", status, data);
            }
        } catch (error) {
            const _error = error as AxiosError<string>;
            console.error("Error en dispatchGetEvents:", _error.message, _error.response?.data);
            return {
                success: false,
                status: _error.response?.status,
                content: null,
            };
        }
        return {
            success: false,
            content: null,
            status: 400,
        };
    }
    async function dispatchGetEventTypes(): Promise<APIResponse<null>> {
        try {
            // Importar security dinámicamente para evitar ciclos
            const security = (await import('../services/security')).default;
            const hasPermiso = await security.checkPermiso("EVENTPT");
            let response;
            if (hasPermiso) {
                response = await API.getEventTypes("EVENTPT");
            } else {
                response = await API.getEventTypes();
            }
            const { status, data } = response;
            if (status === 200) {
                initEventTypes(data.EventTypes.EventType);
                return {
                    success: true,
                    content: null,
                };
            }
        } catch (error) {
            const _error = error as AxiosError<string>;
            return {
                success: false,
                status: _error.response?.status,
                content: null,
            };
        }
        return {
            success: false,
            content: null,
            status: 400,
        };
    }
    async function dispatchListPredios(): Promise<APIResponse<null>> {
        try {
            const { status, data } = await API.listPredios();
            if (status === 200) {
                initPrediosList(data.Entries.Entry);
                return {
                    success: true,
                    content: null,
                };
            }
        } catch (error) {
            const _error = error as AxiosError<string>;
            return {
                success: false,
                status: _error.response?.status,
                content: null,
            };
        }
        return {
            success: false,
            content: null,
            status: 400,
        };
    }
    async function dispatchGetPredios(): Promise<APIResponse<null>> {
        try {
            const { status, data } = await API.getPredios();
            if (status === 200) {
                const datos: PredioGeoJson[] = [];
                data.forEach((element: any) => {
                    datos.push({
                        type: "Feature",
                        properties: {
                            name: element.name,
                            show_on_map: true
                        },
                        geometry: element.geojson
                    });
                });
                initPredios(datos);
                return {
                    success: true,
                    content: null,
                };
            }
        } catch (error) {
            const _error = error as AxiosError<string>;
            return {
                success: false,
                status: _error.response?.status,
                content: null,
            };
        }
        return {
            success: false,
            content: null,
            status: 400,
        };
    }

    async function dispatchGetRegiones(ne_lon: number, ne_lat: number, sw_lon: number, sw_lat: number): Promise<APIResponse<null>> {
        try {
            const { status, data } = await API.getRegiones(ne_lon, ne_lat, sw_lon, sw_lat);
            if (status === 200) {
                const datos: RegionGeoJson[] = [];
                data.forEach((element: any) => {
                    datos.push({
                        type: "Feature",
                        properties: {
                            name: element.name,
                            show_on_map: true
                        },
                        geometry: element.geojson
                    });
                });
                initRegiones(datos);
                return {
                    success: true,
                    content: null,
                };
            }
        } catch (error) {
            const _error = error as AxiosError<string>;
            return {
                success: false,
                status: _error.response?.status,
                content: null,
            };
        }
        return {
            success: false,
            content: null,
            status: 400,
        };
    }

    async function dispatchGetComunas(): Promise<APIResponse<null>> {
        try {
            const { status, data } = await API.getComunas();
            if (status === 200) {
                const datos: ComunaGeoJson[] = [];
                data.forEach((element: any) => {
                    datos.push({
                        type: "Feature",
                        properties: {
                            name: element.name,
                            show_on_map: true
                        },
                        geometry: element.geojson
                    });
                });
                initComunas(datos);
                return {
                    success: true,
                    content: null,
                };
            }
        } catch (error) {
            const _error = error as AxiosError<string>;
            return {
                success: false,
                status: _error.response?.status,
                content: null,
            };
        }
        return {
            success: false,
            content: null,
            status: 400,
        };
    }
    async function dispatchGetUnitsOnline(): Promise<APIResponse<null>> {
        try {
            const { status, data } = await API.getUnitsOnline();
            if (status === 200) {
                const evt = data.Entries;
                const temp: Unit[] = [];
                if (Array.isArray(evt) && evt.length > 0) {
                    evt.forEach((element: any) => {
                        temp.push(element.Entry);
                    });
                }
                initUnitsOnline(temp);
                return {
                    success: true,
                    content: null,
                };
            }
        } catch (error) {
            const _error = error as AxiosError<string>;
            return {
                success: false,
                status: _error.response?.status,
                content: null,
            };
        }
        return {
            success: false,
            content: null,
            status: 400,
        };
    }
    async function dispatchGetCiudades(): Promise<APIResponse<null>> {
        try {
            const { status, data } = await API.getCiudades();
            if (status === 200) {
                const datos: GeoJson[] = [];
                data.forEach((element: any) => {
                    datos.push({
                        type: "Feature",
                        properties: {
                            name: element.name,
                            show_on_map: true
                        },
                        geometry: element.geojson
                    });
                });
                initCiudades(datos);
                return {
                    success: true,
                    content: null,
                };
            }
        } catch (error) {
            const _error = error as AxiosError<string>;
            return {
                success: false,
                status: _error.response?.status,
                content: null,
            };
        }
        return {
            success: false,
            content: null,
            status: 400,
        };
    }

    async function dispatchGetCarreteras(): Promise<APIResponse<null>> {
        try {
            const { status, data } = await API.getCarreteras();
            if (status === 200) {
                const datos: GeoJson[] = [];
                data.forEach((element: any) => {
                    datos.push({
                        type: "Feature",
                        properties: {
                            name: element.name,
                            show_on_map: true
                        },
                        geometry: element.geojson
                    });
                });
                initCarreteras(datos);
                return {
                    success: true,
                    content: null,
                };
            }
        } catch (error) {
            const _error = error as AxiosError<string>;
            return {
                success: false,
                status: _error.response?.status,
                content: null,
            };
        }
        return {
            success: false,
            content: null,
            status: 400,
        };
    }

    async function dispatchGetCaminosPrincipales(): Promise<APIResponse<null>> {
        try {
            const { status, data } = await API.getCaminosPrincipales();
            if (status === 200) {
                const datos: GeoJson[] = [];
                data.forEach((element: any) => {
                    datos.push({
                        type: "Feature",
                        properties: {
                            name: element.name,
                            show_on_map: true
                        },
                        geometry: element.geojson
                    });
                });
                initCaminosPrincipales(datos);
                return {
                    success: true,
                    content: null,
                };
            }
        } catch (error) {
            const _error = error as AxiosError<string>;
            return {
                success: false,
                status: _error.response?.status,
                content: null,
            };
        }
        return {
            success: false,
            content: null,
            status: 400,
        };
    }

    async function dispatchGetCaminosVecinales(): Promise<APIResponse<null>> {
        try {
            const { status, data } = await API.getCaminosVecinales();
            if (status === 200) {
                const datos: GeoJson[] = [];
                data.forEach((element: any) => {
                    datos.push({
                        type: "Feature",
                        properties: {
                            name: element.name,
                            show_on_map: true
                        },
                        geometry: element.geojson
                    });
                });
                initCaminosVecinales(datos);
                return {
                    success: true,
                    content: null,
                };
            }
        } catch (error) {
            const _error = error as AxiosError<string>;
            return {
                success: false,
                status: _error.response?.status,
                content: null,
            };
        }
        return {
            success: false,
            content: null,
            status: 400,
        };
    }

    async function dispatchGetAguas(): Promise<APIResponse<null>> {
        try {
            const { status, data } = await API.getAguas();
            if (status === 200) {
                const datos: GeoJson[] = [];
                data.forEach((element: any) => {
                    datos.push({
                        type: "Feature",
                        properties: {
                            name: element.name,
                            show_on_map: true
                        },
                        geometry: element.geojson
                    });
                });
                initAguas(datos);
                return {
                    success: true,
                    content: null,
                };
            }
        } catch (error) {
            const _error = error as AxiosError<string>;
            return {
                success: false,
                status: _error.response?.status,
                content: null,
            };
        }
        return {
            success: false,
            content: null,
            status: 400,
        };
    }
    async function dispatchListPhotos(filtros: PhotoFilesFilter): Promise<APIResponse<null>> {
        try {
            const { status, data } = await API.listPhotoFiles(filtros)

            if (status == 200) {
                photoFiles.value = data.Entries.Entry
                cantidadPhotos.value = photoFiles.value.length
            } else {
                photoFiles.value = []
                cantidadPhotos.value = 0
            }
        } catch (error) {
            const _error = error as AxiosError<string>;
            console.error(_error.message)
            photoFiles.value = []
            cantidadPhotos.value = 0
        }
        return {
            success: false,
            content: null,
            status: 400,
        };
    }
    return {
        dispatchGetEvents,
        events,
        dispatchGetEventTypes,
        eventTypes,
        initEventTypes,
        predios,
        dispatchGetPredios,
        regiones,
        dispatchGetRegiones,
        comunas,
        dispatchGetComunas,
        ciudades,
        carreteras,
        caminosPrincipales,
        caminosVecinales,
        aguas,
        dispatchGetCiudades,
        dispatchGetCarreteras,
        dispatchGetCaminosPrincipales,
        dispatchGetCaminosVecinales,
        dispatchGetAguas,
        unitsOnline,
        dispatchGetUnitsOnline,
        dispatchListPredios,
        prediosList,
        dispatchListPhotos,
        photoFiles,
        cantidadPhotos,
        dispatchGetEventsPropios,
        eventsPropios
    };
});